import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';

import api from '../../services/api';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { readPage, readItem } from '../../utils/apiResult';
import PoolMembersTab from './tab-pages/PoolMembersTab';
import PoolTagsTab from './tab-pages/PoolTagsTab';
import PoolNotesTab from './tab-pages/PoolNotesTab';
import PoolActivityTab from './tab-pages/PoolActivityTab';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const PoolTitle = styled.h2`
  margin: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$dynamic ? `${p.theme.colors.secondary.main}1A` : `${p.theme.colors.primary.main}1A`)};
  color: ${(p) => (p.$dynamic ? p.theme.colors.secondary.dark : p.theme.colors.primary.dark)};
`;

const RuleCard = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
  padding: 14px 18px;
  margin-bottom: 16px;
`;

const RuleTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const RuleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const CellChip = styled.span`
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// TD-23 (kalan yüzeyler, Faz 2) — bkz. utils/apiResult.js. Ad çözümü başarısız
// olduğunda harita BOŞ dönüyordu ve dinamik havuzun kural özeti departman/lokasyon
// adlarını sessizce ATLIYORDU: İK, havuzun kapsamını EKSİK okuyordu. `ok` artık
// çağırana taşınıyor ve kural kartı üstünde bant gösteriliyor.
async function fetchIdNameMap(url, ids) {
  if (!ids || ids.length === 0) return { ok: true, map: {} };
  const res = await api.get(url, { params: { pageSize: 500 } });
  const { ok, items } = readPage(res);
  const map = {};
  items.forEach((it) => { map[it.id] = it.name; });
  return { ok, map };
}

export default function PoolDetailPage() {
  const { t } = useTranslation();
  const { poolId } = useParams();
  const history = useHistory();

  const [pool, setPool] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [nameMaps, setNameMaps] = useState({ departments: {}, locations: {}, grades: {} });
  const [ruleNamesFailed, setRuleNamesFailed] = useState(false);

  // TD-23 — `catch` dalı ölü koddu (interceptor GET'te reject etmez), yani
  // yüklenemeyen havuzda kullanıcı listeye geri gönderilmiyor, süresiz "..."
  // başlığına bakıyordu. Sinyal artık `status`: bulunamayan/yetkisiz havuzda
  // listeye dönülür.
  const fetchPool = useCallback(async () => {
    const res = await api.get(`/v1/talent-pool/pools/${poolId}`);
    const item = readItem(res);
    if (!item) {
      history.push('/talent/pool');
      return;
    }
    setPool(item);
  }, [poolId, history]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  useEffect(() => {
    if (!pool || pool.poolType !== 'dynamic') return;
    const departmentIds = pool.ruleFilterJson?.departmentIds || [];
    const locationIds = pool.ruleFilterJson?.locationIds || [];
    const gradeIds = pool.ruleFilterJson?.gradeIds || [];
    Promise.all([
      fetchIdNameMap('/v1/departments', departmentIds),
      fetchIdNameMap('/v1/locations', locationIds),
      fetchIdNameMap('/v1/job-grades', gradeIds),
    ]).then(([departments, locations, grades]) => {
      setNameMaps({ departments: departments.map, locations: locations.map, grades: grades.map });
      setRuleNamesFailed(!departments.ok || !locations.ok || !grades.ok);
    });
  }, [pool]);

  const isDynamic = pool?.poolType === 'dynamic';

  const tabs = useMemo(() => [
    { key: 'members', label: t('performance.talentpool.tabMembers') },
    { key: 'notes', label: t('performance.talentpool.tabNotes') },
    { key: 'tags', label: t('performance.talentpool.tabTags') },
    { key: 'activity', label: t('performance.talentpool.tabActivity') },
  ], [t]);

  const ruleFilter = pool?.ruleFilterJson;
  const hasFilterCriteria = ruleFilter && (
    (ruleFilter.departmentIds || []).length > 0
    || ruleFilter.minSeniorityYears != null
    || (ruleFilter.locationIds || []).length > 0
    || (ruleFilter.gradeIds || []).length > 0
  );

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('performance.talentpool.title'), path: '/talent/pool' },
          { label: pool?.name || '...' },
        ]} />

        {pool && (
          <TitleRow>
            <PoolTitle>{pool.name}</PoolTitle>
            <TypeBadge $dynamic={isDynamic}>
              {t(`performance.talentpool.poolType.${pool.poolType}`, { defaultValue: pool.poolType })}
            </TypeBadge>
          </TitleRow>
        )}

        {isDynamic && (
          <RuleCard>
            <RuleTitle>{t('performance.talentpool.ruleSummaryTitle')}</RuleTitle>
            {/* TD-23 — ad çözümü düştüyse kural özeti EKSİK; sessiz kalmak
                havuzun kapsamını olduğundan dar gösteriyordu. */}
            {ruleNamesFailed && <LoadErrorBanner />}
            {(pool.ruleCellKeys || []).length > 0 && (
              <RuleRow>
                {t('performance.talentpool.ruleCellKeys')}:
                {pool.ruleCellKeys.map((k) => (
                  <CellChip key={k}>{t(`performance.ninebox.cell.${k}`, { defaultValue: k })}</CellChip>
                ))}
              </RuleRow>
            )}
            {hasFilterCriteria ? (
              <>
                {(ruleFilter.departmentIds || []).length > 0 && (
                  <RuleRow>
                    {t('performance.talentpool.ruleDepartmentIds')}:
                    {ruleFilter.departmentIds.map((id) => (
                      <CellChip key={id}>{nameMaps.departments[id] || id}</CellChip>
                    ))}
                  </RuleRow>
                )}
                {ruleFilter.minSeniorityYears != null && (
                  <RuleRow>
                    {t('performance.talentpool.ruleMinSeniorityYears')}: {ruleFilter.minSeniorityYears}
                  </RuleRow>
                )}
                {(ruleFilter.locationIds || []).length > 0 && (
                  <RuleRow>
                    {t('performance.talentpool.ruleLocationIds')}:
                    {ruleFilter.locationIds.map((id) => (
                      <CellChip key={id}>{nameMaps.locations[id] || id}</CellChip>
                    ))}
                  </RuleRow>
                )}
                {(ruleFilter.gradeIds || []).length > 0 && (
                  <RuleRow>
                    {t('performance.talentpool.ruleGradeIds')}:
                    {ruleFilter.gradeIds.map((id) => (
                      <CellChip key={id}>{nameMaps.grades?.[id] || id}</CellChip>
                    ))}
                  </RuleRow>
                )}
              </>
            ) : (pool.ruleCellKeys || []).length === 0 && (
              <RuleRow>{t('performance.talentpool.ruleEmpty')}</RuleRow>
            )}
          </RuleCard>
        )}

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {pool && activeTab === 'members' && <PoolMembersTab poolId={poolId} isDynamic={isDynamic} />}
        {pool && activeTab === 'notes' && <PoolNotesTab poolId={poolId} />}
        {pool && activeTab === 'tags' && <PoolTagsTab />}
        {pool && activeTab === 'activity' && <PoolActivityTab poolId={poolId} />}
      </TabContent>
    </PageContainer>
  );
}
